/**
 * What one run is, in words (D11b) — the list row and the detail both read it.
 *
 * PRESENTATION OF STORED FIELDS, NOTHING ELSE. Every function here takes a run
 * as the server sent it and says one of its fields in German. None of them
 * looks anything up, none of them reconstructs a value the run did not store:
 * a tenant the server could not resolve is "nicht verfügbar", never a name
 * guessed from `target_id`; a run without a contract has no contract, never
 * "the tenant's current one"; a system template has no version, never a
 * "Version 1" invented for symmetry. The backend froze what happened (D5–D7,
 * D11a); this file only reads it out.
 *
 * AN UNKNOWN CODE IS SHOWN AS ITSELF, the rule `automationLabels` already
 * follows. The writers own their vocabularies and may grow them; a new skip
 * reason must neither crash the page nor be folded into a German word that
 * might be wrong. It reads as its code until a label is added here.
 *
 * THE CODES ARE THE PRODUCTIVE ONES, inventoried from the writers (D11b):
 * Birthday (`birthday_email_service`), the reminder bundle and its shared
 * provider codes (`automation_execution_service`), and the move-in address
 * (`move_in_address_service`), plus `partial_failure` of the move-in rows from
 * before D6d. `provider_http_<status>` is a claim-ledger code and never reaches
 * a run, so it is not here. S1 added the review's own writer
 * (`move_in_address_review_service`) and its one code, `address_kept`.
 * PC5 added the payment confirmation's writer: its baseline skip, three
 * organisation-level preconditions (transport, sender, Bexio connection),
 * Bexio refusing an active connection's credentials, and the day's stop after a
 * send that went wrong.
 */

/**
 * Why a run was skipped (`skip_reason`) or what went wrong (`error_code`).
 *
 * ONE MAP FOR BOTH COLUMNS, because the two vocabularies do not overlap and a
 * reader asks one question of either: "why". The words are factual — they say
 * what the writer found, not what anybody should do about it, and none of them
 * claims a delivery the system cannot know about.
 */
export const AUTOMATION_RUN_REASON_LABELS = {
  // Birthday — skips.
  opted_out: "Geburtstags-E-Mail abbestellt",
  missing_email: "Keine E-Mail-Adresse hinterlegt",
  invalid_email: "E-Mail-Adresse ungültig",
  // Birthday — template errors.
  template_invalid: "Vorlage ungültig",
  template_render_failed: "E-Mail konnte nicht aus der Vorlage erstellt werden",
  // Birthday and reminder — the provider codes they share (D6c).
  provider_rejected: "Vom E-Mail-Dienst abgelehnt",
  // These two are `indeterminate` runs: the send may or may not have arrived,
  // and the words must not decide it either way.
  provider_indeterminate: "Antwort des E-Mail-Dienstes unklar",
  transport_error: "Verbindung zum E-Mail-Dienst unterbrochen",
  // Move-in address — skips.
  address_already_current: "Adresse bereits aktuell",
  ambiguous: "Objektadresse nicht eindeutig",
  incomplete_object_address: "Objektadresse unvollständig",
  country_conflict: "Widersprüchliches Land in der Objektadresse",
  // The review reasons are named after what the reader has to do or will find
  // (S1); they open the "Prüffall" in the detail.
  manual_address_present: "Neuere Wohnadresse vorhanden",
  manual_review_required: "Wohnadresse prüfen",
  kept_address_protected: "Frühere Adressentscheidung geschützt",
  // Move-in address — an admin's answer to a review (S1): a manual run that
  // wrote nothing because the existing address was kept on purpose.
  address_kept: "Bestehende Adresse beibehalten",
  // Move-in address — the one error.
  processing_failed: "Verarbeitung fehlgeschlagen",
  // Move-in rows from before D6d: one row per organisation and hour.
  partial_failure: "Teilweise fehlgeschlagen",
  // Payment confirmation (PC5) — the one skip: the first pass after every
  // activation records the invoices that already exist and mails none of them.
  activation_baseline: "Bestand abgegrenzt, keine E-Mail",
  // Payment confirmation (PC5) — what stopped an organisation's pass (before
  // its first invoice, or for Bexio and the template also in the middle of it),
  // and a send the transport refused before any request. PC6: no label claims
  // more than its code knows — "unavailable" also covers an active connection
  // whose credentials cannot be loaded, and a sender with only a name or only
  // an address is still incomplete.
  transport_not_configured: "E-Mail-Versand nicht eingerichtet",
  sender_config_missing: "Absender nicht vollständig eingerichtet",
  bexio_connection_unavailable: "Bexio-Verbindung nicht verfügbar",
  bexio_auth_failed: "Bexio-Zugang abgelehnt",
  // ... and a send of the organisation that went wrong earlier the same day.
  send_stopped: "Versand für heute angehalten",
};

export function automationRunReasonLabel(code) {
  if (code === null || code === undefined || code === "") return "";
  return AUTOMATION_RUN_REASON_LABELS[code] || String(code);
}

/**
 * The stored reasons of one run, skip before error, each as `{ code, label }`.
 *
 * A row has at most one of them today; both are kept if a writer ever stores
 * both, rather than silently preferring one.
 */
export function automationRunReasons(run) {
  const out = [];
  for (const code of [run?.skip_reason, run?.error_code]) {
    if (code) out.push({ code, label: automationRunReasonLabel(code) });
  }
  return out;
}

/** True for a code the map above does not know — the label IS the code then. */
export function isKnownAutomationRunReason(code) {
  return Object.prototype.hasOwnProperty.call(AUTOMATION_RUN_REASON_LABELS, code);
}

/**
 * What an `indeterminate` run's two states mean (E25, E42).
 *
 * `finished_at` IS THE ONLY DIFFERENCE, and it is a real one. Without it the
 * run was staged and never closed — the process or its second commit was lost,
 * and nothing local confirms what happened. With it the run was closed with an
 * outcome nobody could establish, typically a provider that did not answer.
 * Neither sentence says "nicht zugestellt": the system does not know that.
 */
export function automationRunStatusNote(run) {
  if (run?.status !== "indeterminate") return "";
  return run.finished_at
    ? "Ausführung mit unklarem Ergebnis abgeschlossen"
    : "Status noch nicht abschließend lokal bestätigt";
}

/**
 * Who or what a run was about, as a display string — never `target_id`.
 *
 *   - the resolved tenant's name;
 *   - a run that names a tenant the server could not resolve (deleted, or not
 *     visible) — a neutral sentence, never the id;
 *   - a run that names another kind of target it could not resolve — the same,
 *     without claiming it was a tenant;
 *   - a run with no target at all: the move-in address's one row per
 *     organisation and hour from before D6d, the only writer that ever wrote
 *     target-less rows.
 *
 * `kind` lets the list style the fallbacks quietly; the text is what is read.
 */
export function automationRunTarget(run) {
  const name = run?.tenant?.name;
  if (name) return { kind: "tenant", text: name };
  if (run?.tenant_id || (run?.target_type === "tenant" && run?.target_id)) {
    return { kind: "unresolved", text: "Mieter nicht verfügbar" };
  }
  // Rows about no single person (PC5): the inventory a fresh activation takes,
  // and a precondition that stopped a whole organisation's pass.
  if (run?.target_type === "activation_baseline") {
    return { kind: "baseline", text: "Bestandsaufnahme bei Aktivierung" };
  }
  if (run?.target_type === "organization") {
    return { kind: "organization", text: "Ganze Organisation" };
  }
  if (run?.target_type || run?.target_id) {
    return { kind: "unresolved", text: "Ziel nicht verfügbar" };
  }
  return { kind: "aggregate", text: "Historischer Sammellauf" };
}

export function isAutomationRunAggregate(run) {
  return automationRunTarget(run).kind === "aggregate";
}

/**
 * The template a send used, as stored on the run (D6b).
 *
 * `system` has no version — the system template is not versioned, and a
 * "Version 1" here would be a number nobody stored. `custom` carries the
 * organisation's version when the server could resolve it. No template stored
 * (a skip, a move-in run) is an empty string, so the caller can say so in its
 * own words rather than this function inventing one.
 */
export function automationRunTemplateLabel(run) {
  const source = run?.template_source;
  if (!source) return "";
  if (source === "system") return "Systemvorlage";
  if (source === "custom") {
    const version = run?.template_version?.version;
    return version !== null && version !== undefined
      ? `Eigene Vorlage · Version ${version}`
      : "Eigene Vorlage";
  }
  return String(source);
}

/**
 * Who started the run (D7 detail `actor`).
 *
 * A schedule has no human behind it, so the answer is the system — a sentence
 * for the reader, not a user record. A stored actor the server could not
 * resolve any more is said as such; nothing is guessed from the id.
 */
export function automationRunActor(run) {
  const actor = run?.actor;
  if (actor && (actor.full_name || actor.email)) {
    return {
      kind: "user",
      name: actor.full_name || actor.email,
      email: actor.full_name && actor.email ? actor.email : "",
    };
  }
  if (actor || run?.actor_user_id) {
    return { kind: "unresolved", name: "Benutzer nicht mehr verfügbar", email: "" };
  }
  if (run?.trigger_type === "schedule") {
    return { kind: "system", name: "System / Automatik", email: "" };
  }
  return { kind: "none", name: "Nicht erfasst", email: "" };
}

/**
 * An address snapshot as display lines, in the spelling that was STORED.
 *
 * Nothing is trimmed, re-cased or normalised: the D11a snapshot is the record
 * of what the run found and left, and "Musterweg 1a" and "musterweg 1A" are two
 * different facts about it. Empty parts are left out rather than printed as
 * "null". The country is shown as stored (an ISO code, usually) — mapping it to
 * a name would be a second vocabulary nobody asked for.
 */
export function automationRunAddressLines(address) {
  if (!address) return [];
  const lines = [];
  if (address.street) lines.push(address.street);
  const place = [address.postal_code, address.city].filter(Boolean).join(" ");
  if (place) lines.push(place);
  if (address.country) lines.push(address.country);
  return lines;
}
